import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';

import { IDP_FLOW_STEPS } from './config';

// Task 35 — IDP akış çubuğu (6 adım: draft→meeting→manager_approval→active→
// period_end_review→completed). "revision_required" kendi adımı değil — manager_approval
// adımı üzerinde uyarı rengi + rozet olarak gösterilir (SubmitAsync revizyondan resubmit'te
// meeting adımını atlayıp doğrudan manager_approval'a döndüğü için akışta ayrı bir kutu
// açmak yanıltıcı olurdu). "cancelled" akış dışı — çubuk yerine tek rozet gösterilir.
// development-plans (yönetici/İK) ve my-development-plans (çalışan) sayfaları arasında
// paylaşılır.

const Bar = styled.div`
  display: flex;
  align-items: center;
  margin: 12px 0 20px;
`;

const StepWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
`;

const Dot = styled.div`
  position: relative;
  z-index: 1;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  background: ${({ $state, theme }) => {
    if ($state === 'revision') return theme.colors.auxiliar.danger;
    if ($state === 'done' || $state === 'current') return theme.colors.primary.main;
    return theme.colors.surface.border;
  }};
  color: ${({ $state, theme }) => ($state === 'pending' ? theme.colors.text.muted : '#fff')};
`;

const Line = styled.div`
  position: absolute;
  top: 13px;
  left: 50%;
  right: -50%;
  height: 2px;
  background: ${({ $done, theme }) => ($done ? theme.colors.primary.main : theme.colors.surface.border)};
  z-index: 0;
`;

const StepLabel = styled.div`
  font-size: 11px;
  margin-top: 6px;
  text-align: center;
  color: ${({ $state, theme }) => ($state === 'pending' ? theme.colors.text.muted : theme.colors.text.primary)};
  font-weight: ${({ $state }) => ($state === 'current' || $state === 'revision' ? 700 : 500)};
`;

const CancelledBadge = styled.div`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  background: ${({ theme }) => `${theme.colors.auxiliar.danger}1A`};
  color: ${({ theme }) => theme.colors.auxiliar.danger};
  margin: 12px 0 20px;
`;

export default function IdpFlowBar({ status }) {
  const { t } = useTranslation();

  if (status === 'cancelled') {
    return <CancelledBadge>{t('performance.idp.status.cancelled')}</CancelledBadge>;
  }

  const currentIndex = IDP_FLOW_STEPS.indexOf(status === 'revision_required' ? 'manager_approval' : status);

  return (
    <Bar>
      {IDP_FLOW_STEPS.map((step, idx) => {
        let state = 'pending';
        if (status === 'revision_required' && step === 'manager_approval') state = 'revision';
        else if (idx < currentIndex) state = 'done';
        else if (idx === currentIndex) state = 'current';

        return (
          <StepWrap key={step}>
            {idx < IDP_FLOW_STEPS.length - 1 && <Line $done={idx < currentIndex} />}
            <Dot $state={state}>{state === 'done' ? <Check size={14} /> : idx + 1}</Dot>
            <StepLabel $state={state}>
              {t(`performance.idp.flow.${step}`)}
              {state === 'revision' && ` (${t('performance.idp.status.revision_required')})`}
            </StepLabel>
          </StepWrap>
        );
      })}
    </Bar>
  );
}
